import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Claim, SourceLink as SourceLinkType } from '../types';
import { billedOf, money } from '../utils';
import { payerPathFor } from '@/features/settings-d/payerRulesMock';
import { PMS } from '@/features/shared/pms';

const BLURB: Record<string, string> = {
  ev: 'From your office’s own claim history with this payer. No other patients’ names are shown.',
  xray: `From ${PMS} imaging (mock preview).`,
  note: `From the clinical record in ${PMS} (mock preview).`,
  claim: 'The claim form as sent (mock preview).',
  chart: `From the periodontal chart in ${PMS} (mock preview).`,
  elig: 'The eligibility check ELVA ran (mock preview).',
  contract: 'From the payer contract on file (mock preview).',
  plan: 'From the benefits on file for this patient (mock preview).',
  visits: 'This patient’s visit history in your office (mock preview).',
};

/** Context a source link can use to open the EOB or link to the payer's rules. */
export interface SrcContext {
  payerName?: string;
  payerShort?: string;
  claimId?: string;
  patient?: string;
  memberId?: string;
  serviceDate?: string;
  decisionDate?: string;
  procedures?: Claim['procedures'];
  paid?: number;
}

/** Build a source context from a claim, for the EOB view and the payer-rules link. */
export function claimSrcContext(claim: Claim): SrcContext {
  return {
    payerName: claim.payer.name,
    payerShort: claim.payer.short,
    claimId: claim.id,
    patient: claim.patient,
    memberId: claim.memberId,
    serviceDate: claim.serviceDate,
    decisionDate: claim.deniedDate ?? claim.paidDate ?? '',
    procedures: claim.procedures,
    ...(claim.paid != null ? { paid: claim.paid } : {}),
  };
}

function kindOf(id: string): string {
  return id.split('-')[0] ?? '';
}

/** Is this source the payer's own appeal rules / address (→ Payer Rules in Settings)? */
function isPayerRule(id: string): boolean {
  return id === 'ev-addr' || id.endsWith('-rules') || id.includes('rules');
}

function blurbFor(id: string): string {
  return BLURB[kindOf(id)] ?? 'ELVA linked this from the payment and the record.';
}

/** The read-only EOB view (mock) — the payer's electronic explanation of benefits. */
function EobView({ label, ctx, onClose }: { label: string; ctx: SrcContext; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const short = ctx.payerShort ?? 'The payer';
  const procs = ctx.procedures ?? [];
  const billed = procs.length ? billedOf(procs) : null;
  const paid = ctx.paid;

  return (
    <div className="apl-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} className="apl-dialog" role="dialog" aria-modal="true" aria-label={label} style={{ maxWidth: 620 }}>
        <div className="apl-card-head" style={{ marginBottom: 0 }}>
          <div>
            <p className="apl-eyebrow">Explanation of benefits · electronic</p>
            <h2 className="apl-dialog-title">{short} EOB{ctx.decisionDate ? ` · ${ctx.decisionDate}` : ''}</h2>
          </div>
          <button type="button" className="apl-btn apl-btn-sm" onClick={onClose}>Close</button>
        </div>

        <dl className="apl-dl">
          {ctx.patient ? (<><dt>Patient</dt><dd>{ctx.patient}{ctx.memberId ? ` · member ${ctx.memberId}` : ''}</dd></>) : null}
          {ctx.claimId ? (<><dt>Claim</dt><dd className="num">{ctx.claimId}{ctx.serviceDate ? ` · service ${ctx.serviceDate}` : ''}</dd></>) : null}
          <dt>Payer</dt><dd>{ctx.payerName ?? short}</dd>
        </dl>

        {procs.length ? (
          <div className="apl-tbl-wrap">
            <table className="apl-tbl">
              <thead>
                <tr>
                  <th>Code</th>
                  <th className="ta-right">Billed</th>
                  <th className="ta-right">{short} paid</th>
                </tr>
              </thead>
              <tbody>
                {procs.map((p, i) => (
                  <tr key={`${p.code}-${String(i)}`}>
                    <td style={{ fontWeight: 700 }}>{p.code}{p.tooth ? ` ${p.tooth}` : ''}</td>
                    <td className="ta-right num">{money(p.fee)}</td>
                    <td className="ta-right num">{paid != null && i === 0 ? money(paid) : i === 0 && paid == null ? '$0' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {billed != null ? (
          <p className="apl-meta" style={{ margin: 0 }}>
            Billed {money(billed)} · {short} paid {money(paid ?? 0)}.
          </p>
        ) : null}
        <p className="apl-meta" style={{ margin: 0 }}>
          Read-only. This is a mock of the electronic EOB {short} sent for this claim; ELVA keeps the original on file.
        </p>
      </div>
    </div>
  );
}

export function SourceLink({ source, context }: { source: SourceLinkType; context?: SrcContext }) {
  const [open, setOpen] = useState(false);
  const [eob, setEob] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const isEob = kindOf(source.id) === 'eob';
  const payerRule = isPayerRule(source.id);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Payer rules / appeal address → open the payer's rules in Settings (D148, not a stub).
  if (payerRule && context?.payerName) {
    return (
      <Link to={payerPathFor(context.payerName)} className="apl-src">
        {source.label}
      </Link>
    );
  }

  // The payer's EOB → open a read-only mock EOB view (not a stub popover).
  if (isEob) {
    return (
      <>
        <button type="button" className="apl-src" onClick={() => { setEob(true); }}>
          {source.label}
        </button>
        {eob ? <EobView label={source.label} ctx={context ?? {}} onClose={() => { setEob(false); }} /> : null}
      </>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" className="apl-src" onClick={() => { setOpen((v) => !v); }}>
        {source.label}
      </button>
      {open ? (
        <div className="apl-popover" role="dialog" aria-label={source.label} style={{ left: 0, top: 22 }}>
          <p className="apl-popover-title">{source.label}</p>
          <p className="apl-meta" style={{ margin: 0 }}>{blurbFor(source.id)}</p>
        </div>
      ) : null}
    </div>
  );
}

export function SourceRow({ sources, context }: { sources?: SourceLinkType[] | undefined; context?: SrcContext }) {
  if (!sources || sources.length === 0) return null;
  return (
    <div className="apl-srcrow">
      {sources.map((s) => (
        <SourceLink key={s.id} source={s} {...(context ? { context } : {})} />
      ))}
    </div>
  );
}
